import React from 'react';
import { ScopeContext } from './scope-context';
import {
  IScopeValue, IScopeValueWithExtraData, ScopeExtraData,
} from '../types';

export type WithoutScope<P> = Omit<P, 'scope'>;

export function withScope<P extends { scope: IScopeValueWithExtraData }>(Component: React.ComponentType<P>): React.FC<WithoutScope<P>> {
  return (props: WithoutScope<P>) => (
    <ScopeContext.Consumer>
      {({ scope }) => {
        // Read extraData directly from the history state on the underlying QueryParam.
        // history is a mutable singleton — push/replace update location.state in-place,
        // so this is always current after any navigation or replace.
        const extraData = scope?.history?.location?.state?.extraData;
        return (
          <ScopeWithExtra
            component={Component}
            props={props as WithoutScope<P>}
            scope={scope ?? null}
            extraData={extraData}
          />
        );
      }}
    </ScopeContext.Consumer>
  );
}

type ScopeWithExtraProps<P> = {
  component: React.ComponentType<P>;
  props: WithoutScope<P>;
  scope: IScopeValue | null;
  extraData: ScopeExtraData;
};

class ScopeWithExtra<P extends { scope: IScopeValueWithExtraData }> extends React.Component<ScopeWithExtraProps<P>> {
  private cachedScope: IScopeValue | null = null;
  private cachedExtraData: ScopeExtraData = undefined;
  private cachedScopeWithExtra: IScopeValueWithExtraData | null = null;

  private getScopeWithExtra(): IScopeValueWithExtraData | null {
    const { scope, extraData } = this.props;
    if (scope === this.cachedScope && extraData === this.cachedExtraData) {
      return this.cachedScopeWithExtra;
    }
    this.cachedScope = scope;
    this.cachedExtraData = extraData;
    this.cachedScopeWithExtra = scope
      ? Object.assign(Object.create(scope as object), { extraData }) as IScopeValueWithExtraData
      : null;
    return this.cachedScopeWithExtra;
  }

  render() {
    const { component: Comp, props } = this.props;
    const scopeWithExtra = this.getScopeWithExtra();
    // When no ScopeContext.Provider is in the tree, scope is null.
    // Omit the prop entirely in that case so consumers receive `undefined`
    // (matching pre-2.1.2 behaviour) rather than an explicit `null`, which
    // would override any defaultProps and break components that don't expect
    // a scope at all (e.g. components rendered in isolated ReactDOM.render roots).
    const scopeProp = scopeWithExtra !== null
      ? { scope: scopeWithExtra }
      : {};
    return (
      <Comp
        {...{ ...props, ...scopeProp } as P}
      />
    );
  }
}
